import { useEffect, useRef, useState } from 'react';

import Text from '@cartwhl/ui/base/Text';
import CircularProgressIndicator from '@cartwhl/ui/messaging/CircularProgressIndicator';

const calculateProgress = (
  estimatedFinishedTime: number,
  exportStartedAt: number,
) => {
  if (Number.isNaN(estimatedFinishedTime) || Number.isNaN(exportStartedAt)) {
    return 0;
  }
  const currentTime = Date.now();
  const totalDuration = estimatedFinishedTime - exportStartedAt;
  const elapsedDuration = currentTime - exportStartedAt;
  const progress = Math.round((100 * elapsedDuration) / totalDuration);
  return Math.min(100, progress);
};

export default function ExportLoader({
  estimatedFinishedAt,
  exportStartedAt,
  fileDownloadPath,
  pollExportStatus,
}: {
  estimatedFinishedAt: Date;
  exportStartedAt: Date;
  fileDownloadPath: string;
  pollExportStatus: (enableAutoDownload: boolean) => void;
}) {
  const [progress, setProgress] = useState(fileDownloadPath ? 100 : 0);

  const pollingStartedRef = useRef(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const progressRef = useRef(progress);

  useEffect(() => {
    if (!estimatedFinishedAt || !exportStartedAt) return;

    const now = Date.now();
    if (progressRef.current < 100 || now < estimatedFinishedAt.getTime()) {
      pollingStartedRef.current = false;
      const interval = setInterval(() => {
        // Check if file is ready for download
        if (fileDownloadPath) {
          setProgress(100);
          clearInterval(interval);
          pollingStartedRef.current = false;
          return;
        }

        const currentProgress = calculateProgress(
          estimatedFinishedAt.getTime(),
          exportStartedAt.getTime(),
        );
        // Cap at 99% until file is ready
        setProgress(Math.min(currentProgress, 99));
        progressRef.current = Math.min(currentProgress, 99);

        // Start polling only once when progress reaches 99%
        if (!pollingStartedRef.current && currentProgress >= 99) {
          pollingStartedRef.current = true;
          pollingIntervalRef.current = setInterval(() => {
            pollExportStatus(true);
          }, 5000);
        }
      }, 250);

      return () => {
        clearInterval(interval);
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    }
  }, [estimatedFinishedAt, exportStartedAt, fileDownloadPath]);

  return (
    <div className="bg-gray-75 flex aspect-video flex-col items-center justify-center rounded-2xl px-8 text-white">
      <CircularProgressIndicator className="mx-auto mb-6" progress={progress} />
      {fileDownloadPath ? (
        <Text
          as="a"
          href={fileDownloadPath}
          variant="studio/body/sm"
          className="max-w-[300px] text-center"
        >
          Export ready! <span className="font-bold">Click here</span> if your
          download did not start automatically.
        </Text>
      ) : (
        <Text
          as="div"
          variant="studio/body/sm"
          className="max-w-[300px] text-center"
        >
          Preparing files for download. Your download will automatically start
          when ready. <br /> <br />
        </Text>
      )}
    </div>
  );
}
